import React, { useState, useEffect } from 'react';
import {
  LegacyAutocomplete,
  LegacyAutocompleteInput,
  LegacyAutocompleteOptions,
  LegacyAutocompleteSelectedOptions,
} from '@flixbus/honeycomb-react';

import { useVoucherFormContext } from '../../Context';
import {
  getCachedBrands,
  convertBrandsToAutocompleteBrands,
  AutocompleteBrand,
} from './brandsCache';

interface BrandRuleEditorProps {
  ruleType: 'brand_allowlist' | 'brand_disallowlist';
  label: string;
}

const BrandRuleEditor: React.FC<BrandRuleEditorProps> = ({ ruleType, label }) => {
  const {
    formState: {
      payload: { rules },
    },
    handleRuleChange,
  } = useVoucherFormContext();

  const [data, setData] = useState<AutocompleteBrand[]>([]);
  const [selectedBrands, setSelectedBrands] = useState<AutocompleteBrand[]>([]);
  const [highlight, setHighlight] = useState('');
  const [allBrands, setAllBrands] = useState<AutocompleteBrand[]>([]);

  useEffect(() => {
    getCachedBrands().then(brands => {
      setAllBrands(convertBrandsToAutocompleteBrands(brands));
    });
  }, []);

  useEffect(() => {
    const brandUuidsState =
      (rules.find(r => r.type === ruleType) as { brands: string[] } | undefined)?.brands || [];

    if (brandUuidsState.length > 0) {
      getCachedBrands().then(brands => {
        const selectedBrandsData = brands
          .filter(brand => brandUuidsState.includes(brand.uuid))
          .map(brand => ({
            title: brand.name,
            subtitle: brand.uuid,
          }));
        setSelectedBrands(selectedBrandsData);
      });
    } else {
      setSelectedBrands([]);
    }
  }, [rules, ruleType]);

  useEffect(() => {
    if (highlight) {
      const lowerQ = highlight.toLowerCase();
      const filtered = allBrands.filter(
        brand =>
          brand.title.toLowerCase().includes(lowerQ) ||
          (brand.subtitle && brand.subtitle.toLowerCase().includes(lowerQ)),
      );
      setData(filtered);
    } else {
      setData([]);
    }
  }, [highlight, allBrands]);

  const handleValueChange = (brands: AutocompleteBrand[]) => {
    const brandUuids = brands.map(brand => brand.subtitle).filter((id): id is string => !!id);
    handleRuleChange(
      {
        type: ruleType,
        brands: brandUuids,
      },
      brandUuids.length === 0,
    );
    setSelectedBrands(brands);
  };

  const handleItemSelect = (selectedBrand: { title: string; subtitle?: string }) => {
    const brandWithUuid = data.find(d => d.title === selectedBrand.title);
    if (brandWithUuid && !selectedBrands.some(brand => brand.subtitle === brandWithUuid.subtitle)) {
      handleValueChange([...selectedBrands, brandWithUuid]);
      setHighlight('');
      setData([]);
    }
  };

  const handleItemDelete = (selectedBrand: { title: string }) => {
    const selectedBrandData = allBrands.find(d => d.title === selectedBrand.title);

    handleValueChange(
      selectedBrands.filter(brand => brand.subtitle !== selectedBrandData?.subtitle),
    );
  };

  const handleReset = () => {
    handleValueChange([]);
  };

  const handleFocus = () => {
    if (allBrands.length > 0 && !highlight) {
      setData(allBrands);
    }
  };

  return (
    <LegacyAutocomplete
      options={data}
      optionsSelected={selectedBrands.map(brand => ({ title: brand.title }))}
      onDebounce={(e: React.ChangeEvent<HTMLInputElement>) => setHighlight(e.target.value)}
      onDelete={handleItemDelete}
      onReset={handleReset}
      onSelect={handleItemSelect}
    >
      <LegacyAutocompleteInput
        id={`autocomplete-brands-${ruleType}`}
        label={label}
        type='search'
        onFocus={handleFocus}
      />
      <LegacyAutocompleteOptions label='Brands' highlightQuery={highlight} />
      <LegacyAutocompleteSelectedOptions
        deleteAllItemsLabel='Clear selection'
        deleteItemLabel='Remove item from selection'
      />
    </LegacyAutocomplete>
  );
};

export default BrandRuleEditor;
